// ============================================================================
// vpn/VpnConnectionDetails.tsx — «Расширенные» на экране подключений:
// QR-код и файл конфигурации для телефона/компьютера. Текст конфига и
// технические параметры НЕ показываются обычному пользователю (ключи, сервер,
// порт остаются внутри файла). SSoT — сервер (GetVpnConfig).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Copy, Download, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSDK } from '@/store/sdkStore';
import { createLogger } from '@/lib/logger';
import { copyToClipboard } from '@/lib/clipboard';
import { vpnConfigExt, vpnConfigFileName } from './vpnConfigFormat';
import { vpnErrorMessage } from './vpnErrors';
import type { VpnClientConfigData } from './vpnTypes';

const log = createLogger('VpnConnectionDetails');

interface VpnConnectionDetailsProps {
  profileCode: string;
  profileName: string;
}

export function VpnConnectionDetails({ profileCode, profileName }: VpnConnectionDetailsProps) {
  const [config, setConfig] = useState<VpnClientConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await getSDK().vpn.getVpnConfig({ profileCode });
      setConfig({
        profileCode: r.profileCode,
        protocol: r.protocol,
        config: r.config,
        configFormat: r.configFormat,
        qrPngBase64: r.qrPngBase64,
        shareLink: r.shareLink,
      });
    } catch (e) {
      log.error('get vpn config for connection', { error: e, code: profileCode });
      setError(vpnErrorMessage(e, 'config'));
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [profileCode]);

  useEffect(() => { void load(); }, [load]);

  const download = () => {
    if (!config) return;
    const blob = new Blob([config.config], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = vpnConfigFileName(profileName || 'vpn', config.configFormat);
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">Получаем настройки подключения…</div>;
  }

  if (error) {
    return (
      <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
        <div className="text-destructive">{error}</div>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          <RefreshCw className="mr-1 h-3.5 w-3.5" /> Повторить
        </Button>
      </div>
    );
  }

  if (!config) return null;

  const isLink = config.configFormat === 'uri';
  const ext = vpnConfigExt(config.configFormat);

  return (
    <div className="space-y-3 rounded-md border bg-muted/20 p-3">
      <div className="text-xs text-muted-foreground">
        {isLink
          ? 'Отсканируйте QR-код в приложении VPN на телефоне или скопируйте ссылку.'
          : 'Отсканируйте QR-код в приложении VPN на телефоне либо скачайте файл настроек и откройте его в приложении.'}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        {config.qrPngBase64 && (
          <img
            alt="QR-код для настройки подключения"
            className="h-[180px] w-[180px] shrink-0 rounded border bg-white p-1"
            src={`data:image/png;base64,${config.qrPngBase64}`}
          />
        )}
        <div className="flex min-w-0 flex-1 flex-wrap content-start gap-2">
          <Button variant="outline" size="sm" onClick={download}>
            <Download className="mr-1 h-3.5 w-3.5" /> Скачать файл .{ext}
          </Button>
          {config.shareLink && (
            <Button variant="outline" size="sm" onClick={() => void copyToClipboard(config.shareLink)}>
              <Copy className="mr-1 h-3.5 w-3.5" /> Скопировать ссылку
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
